import React, { useEffect, useState } from "react";
import StyleFields from "./StyleFields";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import { testerHasPermission } from "../../utils/testerPermissions";
import { adminHasPermission } from "../../utils/adminPermissions";

export const TYPE_LABELS = { worksheet: "Worksheet", mock: "Mock Exam", paper: "Practice Paper" };

export const TYPE_COLORS = {
  worksheet: { bg: "#e0f2fe", fg: "#075985" },
  mock: { bg: "#ede9fe", fg: "#5b21b6" },
  paper: { bg: "#ccfbf1", fg: "#115e59" },
};

export const STATE_CHIPS = {
  completed: { label: "Done", color: "success" },
  in_progress: { label: "Running", color: "info" },
  missing: { label: "Missing", color: "default" },
  failed: { label: "Failed", color: "error" },
  needs_review: { label: "Needs review", color: "warning" },
  flagged: { label: "Flagged", color: "error" },
};

// Students and parents only ever see videos a tester verified and an admin published.
export const VISIBILITY_LABELS = {
  approved: "Only published videos",
  none: "No videos",
};

export const VERIFICATION_CHIPS = {
  approved: { label: "Verified", color: "success" },
  rejected: { label: "Rejected", color: "error" },
  awaiting: { label: "Awaiting verification", color: "warning" },
};

/** Whether the signed-in user may approve or reject explanation videos. */
export const canVerifyVideos = () =>
  (localStorage.getItem("role") === "tester" ? testerHasPermission : adminHasPermission)("verify-explanation_video");

/** A finished video's tester verdict, with the reviewer and notes as tooltip. */
export function VerificationChip({ question }) {
  const chip = VERIFICATION_CHIPS[question.verification || "awaiting"];
  const who = question.verified_by
    ? `${question.verified_by}${question.verified_at ? ` · ${new Date(question.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ""}`
    : "";
  const tip = [who, question.verification_notes].filter(Boolean).join(" — ");
  return (
    <Tooltip title={tip || (question.verification ? "" : "A tester has not watched this video yet")}>
      <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />
    </Tooltip>
  );
}

export const POLL_MS = 10000;

/** "45s", "12 min", "3 h" — how long a job has waited or run. */
export const ago = (seconds) => {
  if (seconds == null) return "";
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${Math.round(seconds / 360) / 10} h`;
};

/** A ready job nobody has taken for this long means no worker is listening on its queue. */
const STALLED_SECONDS = 120;

/**
 * What the video queues hold right now, and a warning when jobs wait with no
 * worker taking them.
 */
export function QueueStatus({ queue, onStopAll, stopping }) {
  if (!queue?.supported) return null;
  const waiting = queue.queues.reduce((n, q) => n + q.waiting, 0);
  const working = queue.queues.reduce((n, q) => n + q.working, 0);
  const longest = Math.max(0, ...queue.queues.map((q) => q.oldest_working_seconds || 0));
  const stalled = queue.queues.filter((q) => q.working === 0 && q.oldest_waiting_seconds > STALLED_SECONDS);

  return (
    <Alert
      severity={stalled.length ? "warning" : "info"}
      variant="outlined"
      sx={{ mb: 1.5, bgcolor: "#fff", "& .MuiAlert-action": { alignItems: "center", pt: 0 } }}
      action={
        onStopAll && waiting + working > 0 ? (
          <Tooltip title="Stop every video being made or waiting, in every set. They show as missing and can be generated again.">
            <span>
              <Button size="small" color="error" variant="outlined" startIcon={<StopIcon />} disabled={stopping} onClick={onStopAll}>
                Stop all {waiting + working}
              </Button>
            </span>
          </Tooltip>
        ) : null
      }
    >
      <Typography variant="body2">
        <b>Video queue:</b> {working} being made · {waiting} waiting
        {longest > 0 && ` · longest running ${ago(longest)}`}
        {queue.failed_last_day > 0 && ` · ${queue.failed_last_day} failed in the last day`}
      </Typography>
      {stalled.map((q) => (
        <Typography key={q.name} variant="body2" sx={{ mt: 0.5 }}>
          Nothing has taken a job from the <code>{q.name}</code> queue for {ago(q.oldest_waiting_seconds)}: its worker is
          probably not running. Check <code>supervisorctl status</code> on the server, or stop these videos and run them
          again later.
        </Typography>
      ))}
    </Alert>
  );
}

export const money = (n, digits = 2) => `$${(n || 0).toFixed(digits)}`;

/** Tiny amounts (one video) need a third decimal to not read as $0.00. */
export const moneyAuto = (n) => money(n, n > 0 && n < 0.1 ? 3 : 2);

export function StateChip({ state, count }) {
  const chip = STATE_CHIPS[state];
  if (!chip) return null;
  return (
    <Chip
      size="small"
      color={chip.color}
      variant={state === "missing" ? "outlined" : "filled"}
      label={count === undefined ? chip.label : `${chip.label} ${count}`}
    />
  );
}

export function TypeChip({ type }) {
  const c = TYPE_COLORS[type] || TYPE_COLORS.worksheet;
  return (
    <Chip
      size="small"
      label={TYPE_LABELS[type] || type}
      sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600, minWidth: 110 }}
    />
  );
}

export function ConfirmGenerate({ confirm, submitting, onClose, onConfirm }) {
  // The run's style (presenter, voice, design, music); {} = all from Settings.
  const [style, setStyle] = useState({});
  useEffect(() => {
    if (confirm) setStyle({});
  }, [confirm]);

  return (
    <Dialog open={Boolean(confirm)} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{confirm?.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {confirm?.count} video(s) for <strong>{confirm?.name}</strong>, about {moneyAuto(confirm?.cost)}.
        </Typography>
        {confirm?.count > 0 && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Rendering takes about {renderTime(confirm.count)} on one worker (≈3 min a video; an anime presenter takes longer).
            {confirm.count > 200 && " For a batch this size, a scheduled overnight run keeps the queue free in the day."}
          </Typography>
        )}
        {confirm?.note && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {confirm.note}
          </Typography>
        )}
        <StyleFields key={confirm?.title} value={style} onChange={setStyle} defaultOpen={Boolean(confirm?.styleOpen)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onConfirm(style)} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : "Queue videos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const SEGMENTS = [
  ["completed", "#16a34a", "Done"],
  ["in_progress", "#3b82f6", "Being made"],
  ["problems", "#ef4444", "Failed, held or flagged"],
];

/** One bar per set: done (green), being made (blue), failed or held (red); the rest is still missing. */
export function VideoProgress({ counts, total }) {
  const values = {
    completed: counts.completed,
    in_progress: counts.in_progress,
    problems: counts.failed + counts.needs_review + counts.flagged,
  };
  const tip = SEGMENTS.filter(([k]) => values[k] > 0)
    .map(([k, , label]) => `${label}: ${values[k]}`)
    .concat(counts.missing > 0 ? [`Missing: ${counts.missing}`] : [])
    .join(" · ");

  return (
    <Tooltip title={tip || "No questions"}>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: "#e5e7eb", overflow: "hidden", display: "flex" }}>
        {SEGMENTS.map(([k, color]) =>
          values[k] > 0 && total > 0 ? <Box key={k} sx={{ width: `${(values[k] / total) * 100}%`, bgcolor: color }} /> : null
        )}
      </Box>
    </Tooltip>
  );
}

/** Rough render time for a batch: about 3 minutes a video on one worker (an anime presenter takes longer). */
export const renderTime = (videos) => {
  const hours = (videos * 3) / 60;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${Math.round(hours)} h`;
  return `${Math.round(hours / 24)} days`;
};
